import { Editor } from 'slate';

import { resolveTableEntryByAnchorId } from './resolve-location-anchor';
import { stampReasoningOnFirstSourcedEdit } from './suggestion-reasoning';
import { serializeRemovedRowsToPreviewHtml } from '../../../serializers/html';
import { AlineTransforms, EditingMode } from '../../internal';
import type { SuggestionSource } from '../../types';

export interface ApplyRemoveTableOutputOptions {
  /**
   * The author ID for the suggested edits (typically the AI or system user)
   */
  authorId: string;
  /**
   * Identifies the whole table removal as one group, so accept/reject acts
   * on it as a unit
   */
  groupId: string;
  /**
   * The table's `table-…` anchor id, as emitted in the LLM-facing document
   * format.
   */
  locationAnchor: string;
  /**
   * Why the table is being removed — stored on the first sourced edit of the
   * removal
   */
  reasoning: string;
  /**
   * The source stamp carried by every marked edit — or undefined to
   * materialize the removal as the author's own tracked changes, with no
   * source stamps and no stored reasoning (no AI chrome).
   */
  source: SuggestionSource | undefined;
}

/**
 * Applies a `remove-table` structured edit output to the editor as a
 * suggested removal of the table identified by its `table-…` anchor id — the
 * AI-sourced equivalent of the "Delete table" command in suggesting mode.
 *
 * Marks every row with a sourced `removeTableRow` edit and strikes all cell
 * content via `deleteTableRows`; accepting removes the rows and then the
 * emptied table itself, rejecting restores it. The reasoning is stored on the
 * first sourced edit — block suggestions carry no suggestion-marker pair.
 *
 * Returns the table's plain text (one line per row) for the chat card
 * preview.
 */
export function applyRemoveTableOutput(
  editor: Editor,
  options: ApplyRemoveTableOutputOptions
): { removedHtml: string } {
  const { authorId, groupId, locationAnchor, reasoning, source } = options;

  const [table, tablePath] = resolveTableEntryByAnchorId(editor, locationAnchor);
  const removedHtml = serializeRemovedRowsToPreviewHtml(table.children);

  AlineTransforms.deleteTableRows(editor, {
    at: Editor.range(editor, tablePath),
    authorId,
    editingMode: EditingMode.SUGGESTING,
    groupId,
    source,
  });

  // A sourceless removal has no sourced edit to stamp — and no AI reasoning
  // belongs on the author's own tracked changes.
  if (source) stampReasoningOnFirstSourcedEdit(editor, { at: tablePath, groupId, reasoning });

  return { removedHtml };
}
